/* eslint-disable no-useless-escape */
import axios from 'axios';
import { AgeCalculator } from '@dipaktelangre/age-calculator';
import jimp from 'jimp';
import xlsx from 'json-as-xlsx/index';
import { useTranslation } from 'react-i18next';
import { createTheme } from '@material-ui/core/styles';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import { version } from '../../../package.json';
import userPendencyQuery from '../queries/PendencySummary';
import {
  COLORS,
  COMMUNICATION_TYPE,
  DEFAULT_API_URL,
  DEFAULT_SMART_REPORT_URL,
  EMPTY_HOUR,
  STATUS_OPENED,
  STATUS_AUDIT,
  HTML_REGEX,
  BROWSER_LANGUAGE,
  OBJECT_KEYS,
  HOURS_IN_MILLI,
} from './constants';
import { getToken } from '../../auth';
import GraphQl from '../../GraphQL';
import updatePendencySummary from '../../redux/actions/pendencyActions';

export function addDoubleQuotes(value) {
  if (value) {
    return `"""${value}"""`;
  }
  return null;
}

export function addQuotes(value) {
  if (value) {
    return `"${value}"`;
  }
  return null;
}

export function isNumber(valor) {
  const rule = /^[0-9]+$/;
  if (!!valor && String(valor).match(rule)) {
    return true;
  }
  return false;
}

export function fixBase64File(base64File) {
  const i = base64File.indexOf('base64,');
  return base64File.slice(i + 7);
}

export async function base64ToFile(base64Data, fileName) {
  const arr = base64Data.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n) {
    n -= 1;
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
}

export async function resizeImage(file) {
  const resizedImage = await jimp.read(URL.createObjectURL(file));
  await resizedImage.resize(768, jimp.AUTO, jimp.RESIZE_BILINEAR);
  await resizedImage.quality(70);
  const base64 = await resizedImage.getBase64Async(file.type);
  const newFile = await base64ToFile(base64, file.name);
  return newFile;
}

export async function upLoadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  let uploadedFile;
  await axios({
    url: `${DEFAULT_API_URL}/uploadfile`,
    method: 'POST',
    headers: {
      Authorization: getToken(),
    },
    data: formData,
  })
    .then((res) => {
      uploadedFile = res.data;
    })
    .catch((error) => {
      console.error(error);
    });
  return uploadedFile;
}

export async function upLoadFile64(file) {
  const formData = new FormData();
  formData.append('file', file);
  let uploadedFile;
  await axios({
    url: `${DEFAULT_API_URL}/uploadfile64`,
    method: 'POST',
    headers: {
      Authorization: getToken(),
    },
    data: formData,
  })
    .then((res) => {
      uploadedFile = res.data;
    })
    .catch((error) => {
      console.error(error);
    });
  return uploadedFile;
}

export function upLoadFilePromise(file) {
  const formData = new FormData();
  formData.append('file', file);
  return axios({
    url: `${DEFAULT_API_URL}/uploadfile`,
    method: 'POST',
    headers: {
      Authorization: getToken(),
    },
    data: formData,
  });
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function calculateAge(dateOfBirth) {
  return AgeCalculator.getAge(new Date(dateOfBirth));
}

export function hourToDate(hour) {
  if (hour === EMPTY_HOUR || hour === null || hour === '') {
    return null;
  }
  return new Date(Date.prototype.setHours.apply(new Date(), hour.split(':')));
}

export function formatYear(dateOfBirth, t) {
  if (dateOfBirth <= new Date().getTime()) {
    const year = calculateAge(dateOfBirth).years.toString();
    if (year === '1') {
      return year.concat(' ', t('general.placeholders.year'), ', ');
    }
    if (year > '1') {
      return year.concat(' ', t('general.placeholders.years'), ', ');
    }
    return '';
  }
  return '';
}

export function formatMonth(dateOfBirth, t) {
  if (dateOfBirth <= new Date().getTime()) {
    const month = calculateAge(dateOfBirth).months.toString();
    if (month === '1') {
      return month.concat(' ', t('general.placeholders.month'), ', ');
    }

    if (month > '1') {
      return month.concat(' ', t('general.placeholders.months'), ', ');
    }

    return '';
  }
  return '';
}

export function formatDays(dateofBirth, t) {
  if (dateofBirth <= new Date().getTime()) {
    const day = calculateAge(dateofBirth).days.toString();
    if (day === '1') {
      return day.concat(' ', t('general.placeholders.day'));
    }

    if (day > '1') {
      return day.concat(' ', t('general.placeholders.days'));
    }
  }
  return '';
}

export function getDateNoTimeZoneFromMiliseconds(dateInMili) {
  return new Date(dateInMili).toLocaleDateString('pt-br', {
    timeZone: 'UTC',
  });
}

export function getUTCDateFromMilliseconds(dateInMilli) {
  const date = new Date(dateInMilli);
  const UTCDate = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
  return new Date(UTCDate);
}

export function convertDateToHourStr(timestampDate) {
  const date = getUTCDateFromMilliseconds(timestampDate);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours < 10 ? `0${hours}` : hours}:${
    minutes < 10 ? `0${minutes}` : minutes
  }`;
}

export function formatPhone(text) {
  if (text.length === 11) {
    return text.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (text.length === 10) {
    text.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return text.replace(/(\d{4})(\d{4})/, '($1) $2-$3');
}

export function onShowAlert(color, title, message, callback) {
  callback({
    status: true,
    title,
    color,
    message,
  });
  window.setTimeout(() => {
    callback({
      status: false,
      title: '',
      color: '',
      message: '',
    });
  }, 5000);
}

export function childrensTree(n, template) {
  let aux = '';
  let par = '';
  for (let i = 1; i <= n; i += 1) {
    aux = `${aux} ${template}`;
    par += '}';
  }
  aux = aux.substring(0, aux.length - 1);
  aux += par;
  return aux;
}

export function generateReport(reportParameters) {
  return axios({
    url: `${DEFAULT_SMART_REPORT_URL}/reports`,
    method: 'POST',
    responseType: 'arraybuffer',
    headers: {
      Authorization: getToken(),
      Accept: 'application/pdf',
    },
    data: reportParameters,
  });
}

export function requestVimeoToken() {
  return axios({
    url: `${DEFAULT_API_URL}/vimeo`,
    method: 'GET',
    headers: {
      Authorization: getToken(),
      Accept: 'application/pdf',
    },
  });
}

export function generateContract(reportParameters) {
  return axios({
    url: `${DEFAULT_SMART_REPORT_URL}/contract`,
    method: 'POST',
    responseType: 'arraybuffer',
    headers: {
      Authorization: getToken(),
      Accept: 'application/pdf',
    },
    data: reportParameters,
  });
}

export async function openReport(reportData) {
  const blob = await new Blob([reportData], {
    type: 'application/pdf',
  });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
}

export function formatCurrency(value) {
  return value.toLocaleString('pt-br', {
    style: 'currency',
    currency: 'BRL',
  });
}

export function removeMaskOfCurrencyFields(value) {
  const formatValue = value.match(/[\d,.]+/g);
  const replacePeriods = formatValue[0].replace(/[.]+/g, '');
  const replaceCommas = replacePeriods.replace(/[,]+/g, '.');
  const finalValue = Number(replaceCommas);
  return finalValue;
}

export const isYoutubeVideo = (url) => {
  const reg =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
  return url.match(reg) ? RegExp.$1 : false;
};

export function youtubeParser(url) {
  const regExp =
    /^https?\:\/\/(?:www\.youtube(?:\-nocookie)?\.com\/|m\.youtube\.com\/|youtube\.com\/)?(?:ytscreeningroom\?vi?=|youtu\.be\/|vi?\/|user\/.+\/u\/\w{1,2}\/|embed\/|watch\?(?:.*\&)?vi?=|\&vi?=|\?(?:.*\&)?vi?=)([^#\&\?\n\/<>"']*)/i;
  const match = url.match(regExp);
  return match && match[1].length === 11 ? match[1] : false;
}

export const isLink = (text) => {
  const regExp = /\b(?:https?|ftp):\/\/[^\s]+/i;
  return regExp.test(text);
};

export function separateLinkFromMessage(message) {
  const regExp =
    /(?:((?:[^\s]|\s+)+?))?(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}(?:\/[^\s]*)?)(?:\s(.*))?/i;
  const match = message.match(regExp);
  if (match) {
    const textBefore = match[1] || '';
    const url = match[2] || match[0];
    const textAfter = match[4] || '';
    return [textBefore, url, textAfter];
  }
  return ['', '', ''];
}

export function loadPendencyQuantity() {
  const query = userPendencyQuery();
  return GraphQl(query, {});
}

export const materialTableTheme = createTheme({
  palette: {
    primary: {
      main: '#9d5fbe',
    },
    secondary: {
      main: '#9d5fbe',
    },
  },
  overrides: {
    MuiToolbar: {
      gutters: {
        paddingLeft: '0px !important',
      },
    },
    MuiCheckbox: {
      colorPrimary: {
        color: '#81d61b',
      },
    },

    MuiTableCell: {
      head: {
        color: '#fff',
        background: '#9d5fbe !important',
      },
    },

    MuiTableSortLabel: {
      root: {
        color: '#fff',
        '&:hover': {
          color: '#debeed',
        },
      },
      active: {
        color: '#debeed !important',
      },
      icon: {
        color: '#debeed !important',
      },
    },
    MuiSvgIcon: {
      root: {
        color: '#6A2B8B',
      },
    },
    MuiTypography: {
      root: {
        color: '#6A2B8B',
        paddingLeft: '0px !important',
      },
      h6: {
        color: '#9d5fbe !important',
        fontSize: '14px',
      },
    },
    MuiInputBase: {
      input: {
        color: '#6A2B8B',
      },
    },
    MuiSelect: {
      icon: {
        color: '#6A2B8B',
      },
    },
    MTableHeader: {
      header: {
        zIndex: 0,
      },
    },
  },
});

export const textEllipsis = {
  maxWidth: '10rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const processObjectValue = (field, item) => {
  const newField = field.split('.').map((f) => `${f}`);

  let value = item;

  newField.forEach((element, index) => {
    value = value[newField[index]] ? value[newField[index]] : '';
  });

  if (value) {
    value = value.toString().replace(/["]|\r?\n|\r|\t/g, '');
  }

  return value || '';
};

const createExportableData = (columns, data) => {
  return data.map((item) =>
    JSON.parse(
      `{${columns.map(
        (column) =>
          `"${column.value}": "${processObjectValue(column.value, item)}"`
      )}}`
    )
  );
};

export function exportXLSX(columns, content, title) {
  const columnsToExportXLSX = columns.map((item) => ({
    label: item.title,
    value: item.field,
  }));

  const settings = {
    sheetName: 'Plan1',
    fileName: title,
    extraLength: 3,
    writeOptions: {},
  };

  const download = true;

  const newData = createExportableData(columnsToExportXLSX, content);
  xlsx(columnsToExportXLSX, newData, settings, download);
}

export function AccountsToReceiveStatus(t, dueDate, status) {
  const due =
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 1,
      23
    ) > new Date(dueDate);
  if (status === STATUS_OPENED && due) {
    return t(`general.vanquished`);
  }
  return t(`general.${status}`);
}

export function dynamicSortAsc(property) {
  return (a, b) => {
    if (a[property] < b[property]) {
      return -1;
    }
    if (a[property] > b[property]) {
      return 1;
    }
    return 0;
  };
}

export function dynamicSortDesc(property) {
  return (a, b) => {
    if (a[property] > b[property]) {
      return -1;
    }
    if (a[property] < b[property]) {
      return 1;
    }
    return 0;
  };
}

export function addQuotesInBlank() {
  return `""`;
}

export function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

export function upLoadFileProvider(file) {
  const formData = new FormData();
  formData.append('file', file);
  return axios({
    url: `${DEFAULT_API_URL}/uploadfile`,
    method: 'POST',
    headers: {
      Authorization: getToken(),
    },
    data: formData,
  });
}

export function ellipsisBetweenText(value, initialLength = 15) {
  if (value) {
    const { length } = value;
    if (length > 20) {
      return `${value.substr(0, initialLength)}...${value.substr(
        length - 10,
        length
      )}`;
    }
  }
  return value;
}

export function colorCommunicationType() {
  const colors = {};
  Object.values(COMMUNICATION_TYPE).forEach((type, index) => {
    colors[type] = COLORS[index];
  });
  return colors;
}

export const getStatusAuditOptions = () => {
  const { t } = useTranslation('common');
  return [
    {
      label: t(`status-audit.${STATUS_AUDIT.PENDING}`),
      value: STATUS_AUDIT.PENDING,
    },
  ];
};

export function getValueWithPadding(value) {
  return String(value).padStart(2, '0');
}

export function getCurrentSelected(activeIndex) {
  const selected = activeIndex + 1;
  if (selected > 9) {
    return String(selected);
  }
  return getValueWithPadding(selected);
}

export function hasFileExtensionByFileName(fileName) {
  return !!fileName.replace(/(.+?)(\.[^.]*$|$)/, '$2');
}

export function getDownloadService(url, fileName) {
  axios.get(url, { responseType: 'blob' }).then((res) => {
    const href = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = href;
    link.download =
      (hasFileExtensionByFileName(fileName) && fileName) || 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
}

export function getPercentage(value, divider) {
  return `${parseFloat(((value / divider) * 100).toFixed(1))}`;
}

export function formatterPercentPieChartDataSet(value, ctx) {
  const datasets = [ctx.chart.data.datasets[0]];
  if (datasets.indexOf(ctx.dataset) === datasets.length - 1) {
    const sum = datasets[0].data.reduce((a, b) => a + b, 0);
    const percentage = getPercentage(value, sum);
    return value ? `${percentage}%` : null;
  }
  return null;
}

export function formatterPercentBarChartDataSet(value, ctx, lengthData) {
  const datasets = [ctx.chart.data.datasets[0]];
  if (datasets.indexOf(ctx.dataset) === datasets.length - 1) {
    const percentage = getPercentage(value, lengthData);
    return value ? `${value} (${percentage}%)` : null;
  }
  return null;
}

export function formatterPercentBarChartTwoDataSet(value, ctx) {
  const otherDatasetIndex = ctx.datasetIndex === 0 ? 1 : 0;
  const total =
    ctx.chart.data.datasets[otherDatasetIndex].data[ctx.dataIndex] + value;
  const percentage = getPercentage(value, total);
  return `${value} (${percentage}%)`;
}

export function removeHtml(value) {
  return value.replace(HTML_REGEX, '');
}

export const getTranslationAnswerByQuantity = (quantity, translate) => {
  let commonTranslate = translate;
  if (!translate) {
    const { t } = useTranslation('common');
    commonTranslate = t;
  }
  return quantity > 1
    ? commonTranslate('general.answers')
    : commonTranslate('general.answer');
};

export function filterByExcludeList(list, excludeList, key) {
  return list.filter((item) => excludeList.indexOf(item[key]) === -1);
}

export function filterByMatchList(list, matchList, key) {
  return list.filter((item) => matchList.indexOf(item[key]) !== -1);
}

export const getUniformed = (translation) => {
  return {
    label: translation('general.uniformed'),
    value: 0,
  };
};

export function getArrayStringify(value) {
  return JSON.stringify(value).replace(/"([^(")"]+)":/g, '$1:');
}

export function getBrowserLanguage() {
  let browserLanguage = navigator.language;
  if (browserLanguage) {
    browserLanguage = browserLanguage.toLowerCase().replace('-', '');
    if (
      !browserLanguage.startsWith(BROWSER_LANGUAGE.PT_BR) &&
      !browserLanguage.startsWith(BROWSER_LANGUAGE.EN_US)
    ) {
      browserLanguage = BROWSER_LANGUAGE.EN_US;
    }
  } else {
    browserLanguage = BROWSER_LANGUAGE.EN_US;
  }
  return browserLanguage;
}

export function getFormattedDate(date) {
  const browserLocale = getBrowserLanguage();
  switch (browserLocale) {
    case BROWSER_LANGUAGE.PT_BR:
      return format(new Date(date), `dd/MM/yyyy`);
    default:
      return format(new Date(date), `yyyy-MM-dd`);
  }
}

export function getFormattedDayMonthDate(date) {
  const browserLocale = getBrowserLanguage();
  switch (browserLocale) {
    case BROWSER_LANGUAGE.PT_BR:
      return format(new Date(date), `dd/MM`);
    default:
      return format(new Date(date), `MM-dd`);
  }
}

export function arrayGroupBy(array, key) {
  return array.reduce((acc, item) => {
    (acc[item[key]] = acc[item[key]] || []).push(item);
    return acc;
  }, {});
}

export function getAnswerGroup(data) {
  const currentQuestions = [...data];
  const questionGroup = arrayGroupBy(data, OBJECT_KEYS.ID);
  return Object.keys(questionGroup).map((key) => {
    const question = currentQuestions.find(
      (current) => current.id === Number(key)
    );
    return {
      ...question,
      answeredGroup: questionGroup[key],
    };
  });
}

export function getRoundPercentageWithSymbol(value) {
  let percentage = value;
  if (value) {
    percentage = Number(value) !== 100 ? value.toPrecision(2) : 100;
  }
  return `${percentage}%`;
}

export function updatePendency(dispatch) {
  loadPendencyQuantity().then(({ userPendency }) => {
    dispatch(updatePendencySummary(userPendency));
  });
}

export function getTranslationAuditStatusByStatus(t, status) {
  return status ? t(`audit-status.${status}`) : null;
}

export function toStringify(value) {
  return JSON.stringify(value).replace(/"([^(")]+)":/g, '$1:');
}

export function removeLabelAndValueObjectArray(array) {
  return array.map(({ value, label, ...rest }) => rest);
}

export function getCurrentVersion() {
  return `V: ${version}`;
}

export function toStringifyWithoutQuotes(value) {
  return JSON.stringify(value).replace(/['"]+/g, '');
}

export function getFormattedDateEN(date) {
  return format(new Date(date), `yyyy-MM-dd`);
}

export function getFileId(url) {
  const brokenUrl = url.split('/');
  return brokenUrl[brokenUrl.length - 1];
}

export function getTimeZoneDate(date) {
  const dateUtc = new Date(date).getTime();
  return new Date(dateUtc - HOURS_IN_MILLI.THREE);
}

export function getDateWithoutTime(dateInMilli) {
  return new Date(
    `${new Date(dateInMilli).toLocaleDateString('lt-LT', {
      timeZone: 'UTC',
    })}T00:00:00`
  );
}

export function getDateAndDayUTC(dateInMilli) {
  return format(getDateWithoutTime(dateInMilli), 'dd/MM - EEE', {
    locale: ptBR,
  });
}

export function newUTCDate() {
  const now = new Date();
  return new Date(now.getTime() + now.getTimezoneOffset() * 60000);
}

export function addTimeZoneOffset(time) {
  const hour = new Date(time);
  hour.setHours(hour.getHours() + 3);
  return hour;
}

export function separateDateAndHourWithoutOffset(dateTimeString) {
  const [date, time] = dateTimeString.split(' ');

  const confirmationHour = new Date(`1970-01-01 ${time}`);
  confirmationHour.setHours(confirmationHour.getHours() - 3);
  const hours = confirmationHour.getHours();
  const minutes = confirmationHour.getMinutes();
  const seconds = confirmationHour.getSeconds();

  const formattedTime = `${hours < 10 ? `0${hours}` : hours}:${
    minutes < 10 ? `0${minutes}` : minutes
  }:${seconds < 10 ? `0${seconds}` : seconds}`;

  return [`${date} ${formattedTime}`];
}
